import React, { useState, useEffect, useCallback } from 'react'
import { apiGet } from '../../utils/api'
import { ZohoChat } from './ZohoChatsView'
import { ChatDetailPanel } from './ChatDetailPanel'

interface BrowseTabProps {
  stats: { total_chats: number } | null
}

interface LinkedChatInfo {
  customerId: number
  customerName: string
}

export const BrowseTab: React.FC<BrowseTabProps> = ({ stats: _stats }) => {
  const [chats, setChats] = useState<ZohoChat[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedChat, setSelectedChat] = useState<ZohoChat | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [linkedChats, setLinkedChats] = useState<Record<string, LinkedChatInfo>>({})
  const pageSize = 50

  // Fetch all zoho channel links once to build a lookup map
  useEffect(() => {
    (async () => {
      try {
        const data = await apiGet<any[]>('/paid-customers/zoho-links')
        const map: Record<string, LinkedChatInfo> = {}
        if (Array.isArray(data)) {
          data.forEach((link: any) => {
            map[String(link.channel_ref_id)] = {
              customerId: link.paid_customer_id,
              customerName: link.company_name || 'Customer #' + link.paid_customer_id,
            }
          })
        }
        setLinkedChats(map)
      } catch {
        // Endpoint may not exist yet — fail silently
      }
    })()
  }, [])

  const fetchChats = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, string | number> = {
        page,
        limit: pageSize,
      }
      if (searchQuery.trim()) {
        params.search = searchQuery.trim()
      }
      const data = await apiGet<{ chats: ZohoChat[]; total: number; page: number; totalPages: number }>(
        '/zoho-chats',
        params
      )
      setChats(data.chats || [])
      setTotalPages(data.totalPages || 1)
      setTotalCount(data.total || 0)
    } catch (err) {
      console.error('Failed to fetch zoho chats:', err)
    } finally {
      setLoading(false)
    }
  }, [page, searchQuery])

  useEffect(() => {
    fetchChats()
  }, [fetchChats])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
    fetchChats()
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - d.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    if (days === 0) return 'Today'
    if (days === 1) return 'Yesterday'
    if (days < 7) return d.toLocaleDateString('en-GB', { weekday: 'short' })
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  return (
    <div className="h-full flex">
      {/* ─── Chat list panel ─────────────────────────────────────── */}
      <div className="w-[380px] flex-shrink-0 border-r border-border flex flex-col h-full bg-surface-primary">
        {/* Search header */}
        <div className="px-4 pt-3 pb-2 border-b border-border">
          <form onSubmit={handleSearch} className="relative">
            <svg
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-txt-secondary pointer-events-none"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              placeholder="Search by name, email, chat #..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              name="zoho-chat-search"
              className="w-full pl-10 pr-4 py-1.5 text-sm rounded-lg border border-border bg-surface-secondary text-txt-primary placeholder-txt-secondary focus:outline-none focus:ring-1 focus:ring-brand-600/30 focus:border-brand-600 dark:bg-surface-tertiary"
            />
          </form>
          <div className="flex items-center justify-between mt-2">
            <span className="text-[11px] text-txt-secondary font-medium">
              {(totalCount ?? 0).toLocaleString()} chats
            </span>
            <span className="text-[11px] text-txt-secondary">
              Page {page} / {totalPages}
            </span>
          </div>
        </div>

        {/* Chat list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <svg className="animate-spin w-6 h-6 text-brand-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          ) : chats.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-txt-secondary">
              <svg className="w-12 h-12 mb-3 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
              </svg>
              <p className="text-sm font-medium">No chats found</p>
              <p className="text-xs mt-1">Try scanning your PDF folder or adjusting search</p>
            </div>
          ) : (
            chats.map((chat) => {
              const isSelected = selectedChat?.id === chat.id
              const initials = (chat.visitor_name || 'U')
                .split(/\s+/)
                .map((w) => w[0])
                .join('')
                .slice(0, 2)
                .toUpperCase()
              return (
                <button
                  key={chat.id}
                  onClick={() => setSelectedChat(chat)}
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-border/40 transition-all duration-100 ${
                    isSelected
                      ? 'bg-brand-600/10 border-l-[3px] border-l-brand-600'
                      : 'hover:bg-surface-secondary/70 border-l-[3px] border-l-transparent'
                  }`}
                >
                  {/* Avatar */}
                  <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${
                    isSelected
                      ? 'bg-brand-600 text-white'
                      : 'bg-brand-600/15 text-brand-600'
                  }`}>
                    {initials}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13px] font-semibold text-txt-primary dark:text-white truncate">
                        {chat.visitor_name || 'Unknown Visitor'}
                      </span>
                      <span className="flex-shrink-0 text-[10px] text-txt-secondary">
                        {formatDate(chat.chat_date)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span className="text-[11px] font-mono text-txt-secondary">
                        #{chat.chat_number}
                      </span>
                      {chat.department && (
                        <span className="text-[10px] text-brand-600 bg-brand-600/10 px-1.5 py-px rounded font-medium">
                          {chat.department}
                        </span>
                      )}
                      {linkedChats[String(chat.id)] && (
                        <span className="text-[10px] text-green-700 bg-green-100 px-1.5 py-px rounded font-medium flex items-center gap-0.5" title={`Linked to ${linkedChats[String(chat.id)].customerName}`}>
                          <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                          {linkedChats[String(chat.id)].customerName.length > 18
                            ? linkedChats[String(chat.id)].customerName.slice(0, 18) + '…'
                            : linkedChats[String(chat.id)].customerName}
                        </span>
                      )}
                      {chat.message_count > 0 && (
                        <span className="text-[10px] text-txt-secondary ml-auto">
                          {chat.message_count} msgs
                        </span>
                      )}
                    </div>
                    {chat.first_message && (
                      <p className="text-[11px] text-txt-secondary mt-1 line-clamp-1 leading-snug">
                        {chat.first_message}
                      </p>
                    )}
                  </div>
                </button>
              )
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-border bg-surface-primary">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-border text-txt-secondary hover:bg-surface-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <div className="flex items-center gap-1">
              {page > 2 && (
                <button onClick={() => setPage(1)} className="w-7 h-7 text-xs rounded-md hover:bg-surface-secondary text-txt-secondary">1</button>
              )}
              {page > 3 && <span className="text-xs text-txt-secondary">...</span>}
              {page > 1 && (
                <button onClick={() => setPage(page - 1)} className="w-7 h-7 text-xs rounded-md hover:bg-surface-secondary text-txt-secondary">{page - 1}</button>
              )}
              <button className="w-7 h-7 text-xs rounded-md bg-brand-600 text-white font-semibold">{page}</button>
              {page < totalPages && (
                <button onClick={() => setPage(page + 1)} className="w-7 h-7 text-xs rounded-md hover:bg-surface-secondary text-txt-secondary">{page + 1}</button>
              )}
              {page < totalPages - 2 && <span className="text-xs text-txt-secondary">...</span>}
              {page < totalPages - 1 && (
                <button onClick={() => setPage(totalPages)} className="w-7 h-7 text-xs rounded-md hover:bg-surface-secondary text-txt-secondary">{totalPages}</button>
              )}
            </div>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 text-xs font-medium rounded-md border border-border text-txt-secondary hover:bg-surface-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* ─── Detail / PDF viewer panel ────────────────────────── */}
      <div className="flex-1 overflow-hidden">
        {selectedChat ? (
          <ChatDetailPanel chat={selectedChat} />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-txt-secondary bg-surface-secondary/30">
            <div className="w-20 h-20 rounded-full bg-surface-secondary flex items-center justify-center mb-4">
              <svg className="w-10 h-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-txt-primary dark:text-white/70">Select a chat to view details</p>
            <p className="text-xs mt-1.5 text-txt-secondary/70">Choose a conversation from the list</p>
          </div>
        )}
      </div>
    </div>
  )
}
